import { LoadingButton } from "@mui/lab";
import { Tooltip } from "@mui/material";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { AnimatePresence, motion } from "motion/react";
import { MutableRefObject, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbCancel, TbInfoCircle, TbProgressCheck, TbTableRow } from "react-icons/tb";

import { usePlanActions, usePlanChanges } from "@/stores/use-plan-table";

import PlanUpdateDialog from "./PlanUpdateDialog";

function PlanChangesButton({
    apiRef,
    totals,
}: {
    apiRef: MutableRefObject<GridApiPremium>;
    totals: {
        row: number;
        cell: number;
    };
}) {
    const { t } = useTranslation();
    const changes = usePlanChanges();
    const changedKeys = useMemo(() => Object.keys(changes || {}), [changes]);

    const { handleRowModesModelCancel, handleShowConfirmationList } = usePlanActions();
    const handleCancelChanges = () => {
        for (const id of Object.keys(changes)) {
            handleRowModesModelCancel(id, apiRef.current);
        }
    };

    const handleSaveChanges = () => {
        if (!totals.row) return;
        handleShowConfirmationList(changedKeys);
    };

    return (
        <>
            <AnimatePresence>
                {!!totals.row && (
                    <>
                        <LoadingButton
                            className=" grow-0 shrink-0 max-w-fit"
                            component={motion.div}
                            initial={{
                                y: -100,
                                opacity: 0,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            exit={{ y: 20, opacity: 0 }}
                            variant="contained"
                            color="primary"
                            disabled={totals.row === 0}
                            fullWidth
                            onClick={handleSaveChanges}
                            startIcon={<TbProgressCheck />}
                        >
                            {t("buttons.saveChanges")}
                        </LoadingButton>
                        <LoadingButton
                            className=" grow-0 shrink-0 max-w-fit"
                            component={motion.div}
                            initial={{
                                x: 100,
                                opacity: 0,
                            }}
                            animate={{
                                opacity: 1,
                                x: 0,
                            }}
                            exit={{ y: 20, opacity: 0 }}
                            variant="outlined"
                            color="secondary"
                            disabled={totals.row === 0}
                            fullWidth
                            onClick={handleCancelChanges}
                            startIcon={<TbCancel />}
                        >
                            {t("buttons.cancelChanges")}
                        </LoadingButton>
                    </>
                )}
            </AnimatePresence>
            <div className=" min-h-[37px]  grow-0 shrink-0 flex gap-1 items-center bg-gray-100 px-2 py-1 rounded ">
                <TbTableRow className=" translate-y-[1px]" /> {totals.row}
                <Tooltip
                    title={
                        <div className="flex flex-col gap-1">
                            <div>{t("changeRowCount") + " " + totals.row}</div>
                            <div>{t("changeCellCount") + " " + totals.cell}</div>
                        </div>
                    }
                >
                    <span className="  !self-start !translate-x-1">
                        <TbInfoCircle size={12} />
                    </span>
                </Tooltip>
            </div>

            <PlanUpdateDialog apiRef={apiRef} />
        </>
    );
}

export default PlanChangesButton;
